import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { APP_URL } from '@/lib/config';

interface ClientAuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roleLoading: boolean;
  isClient: boolean;
  signUp: (email: string, password: string, clientData: { nome: string; telefone?: string }) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  linkClientAccount: (clientId: string) => Promise<void>;
}

const ClientAuthContext = createContext<ClientAuthContextType | undefined>(undefined);

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    const resolveRole = async (uid: string | null) => {
      if (!uid) {
        setIsClient(false);
        setRoleLoading(false);
        return;
      }
      setRoleLoading(true);
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('user_id', uid)
          .maybeSingle();
        setIsClient(profile?.user_type === 'cliente');
      } catch (err) {
        console.error('Erro ao verificar user_type:', err);
        setIsClient(false);
      } finally {
        setRoleLoading(false);
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setRoleLoading(true);
        setTimeout(() => { resolveRole(session?.user?.id ?? null); }, 0);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      resolveRole(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);


  const signUp = async (email: string, password: string, clientData: { nome: string; telefone?: string }) => {
    const redirectUrl = `${APP_URL}/client-portal`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: clientData.nome,
          phone: clientData.telefone,
          user_type: 'cliente'
        }
      }
    });

    if (!error && data.user) {
      // Update profile to mark as client
      await supabase
        .from('profiles')
        .update({ user_type: 'cliente' })
        .eq('user_id', data.user.id);
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const linkClientAccount = async (clientId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('clientes')
        .update({ user_id: user.id })
        .eq('id', clientId);

      if (error) throw error;

      toast.success('Conta vinculada com sucesso!');
    } catch (error: any) {
      toast.error('Erro ao vincular conta: ' + error.message);
    }
  };

  return (
    <ClientAuthContext.Provider value={{
      user,
      session,
      loading,
      roleLoading,
      isClient,
      signUp,
      signIn,
      signOut,
      linkClientAccount
    }}>
      {children}
    </ClientAuthContext.Provider>

  );
}

export function useClientAuth() {
  const context = useContext(ClientAuthContext);
  if (context === undefined) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
}
